import React, { useEffect, useState, Fragment, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Input, 
  InputNumber,
  Select, 
  Button, 
  Card, 
  Row, 
  Col, 
  Tag, 
  Avatar, 
  Rate, 
  Switch, 
  Spin, 
  Pagination, 
  Typography, 
  Space, 
  Drawer,
  Badge,
  Skeleton,
  Empty
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined, 
  UserOutlined, 
  ClockCircleOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useNavigateWithScroll } from '../../hooks/useNavigateWithScroll';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { ApiDefinition } from '../../config';
import { MentorSearchRequest, MentorSearchResponse, AdvertisementPosition, AdvertisementResponse, AdvertisementSlotResponse, AdvertisementType } from '../../types';
import { filterQueryKeys, mentorSearchQueryKeys } from '../../lib/queryClient';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import FilterSortBar from '../../components/Common/FilterSortBar';
import { slotMatchesAdvertisementArea, storeAdvertisementIntent } from '../../utils/advertisementSlots';
import { getAdvertisementImageSrc } from '../../utils/advertisementImage';

const { Title, Text } = Typography;
const { Option } = Select;

function parseMentorParamsFromUrl(sp: URLSearchParams): MentorSearchRequest {
  return {
    page: sp.get('page') ? Number(sp.get('page')) : 1,
    pageSize: sp.get('pageSize') ? Number(sp.get('pageSize')) : 12,
    searchTerm: sp.get('search') || undefined,
    category: sp.get('category') || undefined,
    minPrice: sp.get('minPrice') ? Number(sp.get('minPrice')) : undefined,
    maxPrice: sp.get('maxPrice') ? Number(sp.get('maxPrice')) : undefined,
    minExperience: sp.get('minExperience') ? Number(sp.get('minExperience')) : undefined,
    maxExperience: sp.get('maxExperience') ? Number(sp.get('maxExperience')) : undefined,
    skill: sp.get('skill') || undefined,
    company: sp.get('company') || undefined,
    hasAvailability: sp.get('hasAvailability') === 'true' ? true : undefined,
    sortBy: sp.get('sortBy') || undefined,
    sortOrder: sp.get('sortOrder') || undefined
  };
}

const MentorSearch: React.FC = () => {
  const navigate = useNavigateWithScroll();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isCompany = user?.role === 'Company';
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState<Pick<MentorSearchRequest, 'minPrice' | 'maxPrice' | 'minExperience' | 'maxExperience' | 'skill' | 'company' | 'hasAvailability'>>({
    minPrice: undefined,
    maxPrice: undefined,
    minExperience: undefined,
    maxExperience: undefined,
    skill: undefined,
    company: undefined,
    hasAvailability: undefined
  });
  const [searchParams, setSearchParams] = useState<MentorSearchRequest>(() =>
    parseMentorParamsFromUrl(urlSearchParams)
  );
  const isMentor = user?.role === 'Mentor';

  const debouncedSearchParams = useDebouncedValue(searchParams, ApiDefinition.searchDebounceMs);

  const { data: filters } = useQuery({
    queryKey: filterQueryKeys.mentorSearch,
    queryFn: async () => {
      const response = await apiService.getMentorSearchFilters();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: searchResult, isLoading: searchLoading } = useQuery({
    queryKey: mentorSearchQueryKeys.search(debouncedSearchParams),
    queryFn: async () => {
      const response = await apiService.searchMentors(debouncedSearchParams);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 dakika
  });

  const { data: bannerAds = [] } = useQuery({
    queryKey: mentorSearchQueryKeys.advertisements(AdvertisementType.Banner),
    queryFn: async () => {
      const response = await apiService.getActiveAdvertisements(AdvertisementType.Banner, '/mentor-search', AdvertisementPosition.Top);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: cardAds = [] } = useQuery({
    queryKey: mentorSearchQueryKeys.advertisements(AdvertisementType.Card),
    queryFn: async () => {
      const response = await apiService.getActiveAdvertisements(AdvertisementType.Card, '/mentor-search', AdvertisementPosition.Inline);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { data: adSlots = [] } = useQuery({
    queryKey: ['advertisement-slots-public', '/mentor-search'],
    queryFn: async () => {
      const response = await apiService.getPublicAdvertisementSlots('/mentor-search');
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60,
  });

  const matchingBannerSlot = useMemo(
    () =>
      adSlots.find((slot: AdvertisementSlotResponse) =>
        slotMatchesAdvertisementArea(slot, '/mentor-search', AdvertisementType.Banner, AdvertisementPosition.Top)
      ) ?? null,
    [adSlots]
  );

  const matchingCardSlot = useMemo(
    () =>
      adSlots.find((slot: AdvertisementSlotResponse) =>
        slotMatchesAdvertisementArea(slot, '/mentor-search', AdvertisementType.Card, AdvertisementPosition.Inline)
      ) ?? null,
    [adSlots]
  );

  const mentors = searchResult?.data ?? [];
  const pagination = {
    current: searchResult?.pageNumber ?? 1,
    pageSize: searchResult?.pageSize ?? 12,
    total: searchResult?.totalCount ?? 0
  };


  useEffect(() => {
    const next = parseMentorParamsFromUrl(urlSearchParams);
    setSearchParams(prev =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, [urlSearchParams]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchParams.searchTerm) params.search = searchParams.searchTerm;
    if (searchParams.category) params.category = searchParams.category;
    if (searchParams.minPrice != null) params.minPrice = String(searchParams.minPrice);
    if (searchParams.maxPrice != null) params.maxPrice = String(searchParams.maxPrice);
    if (searchParams.minExperience != null) params.minExperience = String(searchParams.minExperience);
    if (searchParams.maxExperience != null) params.maxExperience = String(searchParams.maxExperience);
    if (searchParams.skill) params.skill = searchParams.skill;
    if (searchParams.company) params.company = searchParams.company;
    if (searchParams.hasAvailability === true) params.hasAvailability = 'true';
    if (searchParams.sortBy) params.sortBy = searchParams.sortBy;
    if (searchParams.sortOrder) params.sortOrder = searchParams.sortOrder;
    if (searchParams.page !== 1) params.page = String(searchParams.page);
    if (searchParams.pageSize !== 12) params.pageSize = String(searchParams.pageSize);
    setUrlSearchParams(params, { replace: true });
  }, [searchParams]);

  const handleAdClick = async (ad: AdvertisementResponse) => {
    // Tıklama sayısını kaydet
    await apiService.recordAdvertisementClick(ad.id);
    
    if (ad.linkUrl) {
      // URL varsa verilen URL'e git
      window.open(ad.linkUrl, '_blank');
    } else {
      // URL yoksa mentor profil detayına git (yeni sekmede)
      window.open(`/mentor/${ad.mentorId}`, '_blank');
    }
  };

  const handleSearch = () => {
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = useCallback((key: string, value: any) => {
    setSearchParams(prev => ({ ...prev, [key]: value, page: 1 }));
  }, []);

  const handleDrawerOpen = useCallback(() => {
    setDrawerFilters({
      minPrice: searchParams.minPrice,
      maxPrice: searchParams.maxPrice,
      minExperience: searchParams.minExperience,
      maxExperience: searchParams.maxExperience,
      skill: searchParams.skill,
      company: searchParams.company,
      hasAvailability: searchParams.hasAvailability
    });
    setFilterDrawerVisible(true);
  }, [searchParams.minPrice, searchParams.maxPrice, searchParams.minExperience, searchParams.maxExperience, searchParams.skill, searchParams.company, searchParams.hasAvailability]);

  const handleDrawerApply = useCallback(() => {
    setSearchParams(prev => ({ ...prev, ...drawerFilters, page: 1 }));
    setFilterDrawerVisible(false);
  }, [drawerFilters]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize
    }));
  };

  const handleMentorClick = (mentorId: number) => {
    navigate(`/mentor/${mentorId}`);
  };

  const handleResetFilters = () => {
    setSearchParams({
      page: 1,
      pageSize: 12,
      searchTerm: '',
      category: undefined,
      company: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      minExperience: undefined,
      maxExperience: undefined,
      skill: undefined,
      hasAvailability: undefined
    });
    setDrawerFilters(prev => ({
      ...prev,
      minPrice: undefined,
      maxPrice: undefined,
      minExperience: undefined,
      maxExperience: undefined,
      skill: undefined,
      company: undefined,
      hasAvailability: undefined
    }));
  };

  const SORT_OPTIONS = [
    { value: '', label: 'Varsayılan (En yeni)' },
    { value: 'createdAt_asc', label: 'En eski' },
    { value: 'rating_desc', label: 'En yüksek puan' },
    { value: 'rating_asc', label: 'En düşük puan' },
    { value: 'hourlyRate_asc', label: 'Ücret (düşük → yüksek)' },
    { value: 'hourlyRate_desc', label: 'Ücret (yüksek → düşük)' },
    { value: 'experienceYears_desc', label: 'Deneyim (çok → az)' },
    { value: 'experienceYears_asc', label: 'Deneyim (az → çok)' },
    { value: 'name_asc', label: 'İsim (A-Z)' },
    { value: 'name_desc', label: 'İsim (Z-A)' }
  ];
  const handleSortChange = (value?: string) => {
    if (!value) {
      setSearchParams(prev => ({ ...prev, sortBy: undefined, sortOrder: undefined, page: 1 }));
      return;
    }
    const [sortBy, sortOrder] = value.split('_');
    setSearchParams(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
  };
  const currentSortValue = searchParams.sortBy && searchParams.sortOrder
    ? `${searchParams.sortBy}_${searchParams.sortOrder}`
    : '';

  const renderMentorCard = (mentor: MentorSearchResponse) => {
    const imageUrl = apiService.getMentorProfileImageUrl(mentor.id);
    
    return (
      <Card
        key={mentor.id}
        className="app-card"
        style={{ height: '100%', cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ padding: '18px', textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}
        onClick={() => handleMentorClick(mentor.id)}
        role="button"
        tabIndex={0}
        aria-label={`${mentor.firstName} ${mentor.lastName} mentor profilini görüntüle`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleMentorClick(mentor.id);
          }
        }}
      >
        {/* Profil Resmi - Yuvarlak - Lazy Loading */}
        <div style={{ marginBottom: '12px', minHeight: '80px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Avatar 
            size={80} 
            src={imageUrl}
            icon={<UserOutlined />}
            style={{ border: '3px solid var(--border-light)' }}
            alt={`${mentor.firstName} ${mentor.lastName} profil resmi`}
            onError={() => {
              // Resim yüklenemezse default icon göster
              return true;
            }}
          />
        </div>
        <div style={{ marginBottom: '12px', minHeight: '50px' }}>
          <Title level={4} style={{ margin: 0, marginBottom: '4px', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)' }}>
            {mentor.firstName} {mentor.lastName}
          </Title>
          {mentor.title && (
            <Text type="secondary" style={{ fontSize: 'clamp(0.8rem, 1.5vw, 0.875rem)' }}>
              {mentor.title}
            </Text>
          )}
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            {mentor.hourlyRate && (
              <div style={{ marginBottom: '8px' }}>
                <Text strong style={{ color: '#059669' }}>
                  ₺{mentor.hourlyRate}/saat
                </Text>
              </div>
            )}

            {mentor.experienceYears && (
              <div style={{ marginBottom: '12px' }}>
                <ClockCircleOutlined style={{ marginRight: '4px', color: 'var(--text-secondary)' }} />
                <Text type="secondary">{mentor.experienceYears} yıl deneyim</Text>
              </div>
            )}

            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '4px' }}>
              <Rate disabled value={mentor.rating ?? 0} style={{ fontSize: '14px' }} allowHalf />
              <Text type="secondary" style={{ marginLeft: '4px' }}>
                {typeof mentor.rating === 'number' && mentor.rating > 0
                  ? `${Number(mentor.rating).toFixed(1)} (${mentor.reviewCount ?? 0} değerlendirme)`
                  : `Henüz değerlendirme yok`}
              </Text>
            </div>

            <div style={{ marginBottom: '12px', minHeight: '32px' }}>
              {mentor.skills.slice(0, 3).map((skill, index) => (
                <Tag key={index} color="blue" style={{ margin: '2px' }}>
                  {skill}
                </Tag>
              ))}
              {mentor.skills.length > 3 && (
                <Tag color="default">+{mentor.skills.length - 3}</Tag>
              )}
            </div>

            {mentor.bio && (
              <Text type="secondary" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)', display: 'block', minHeight: '36px' }}>
                {mentor.bio.length > 100 ? `${mentor.bio.substring(0, 100)}...` : mentor.bio}
              </Text>
            )}
          </div>

          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Badge 
              status={mentor.hasAvailability ? 'success' : 'default'} 
              text={mentor.hasAvailability ? 'Müsait' : 'Müsait Değil'} 
            />
            <Button 
              type="primary" 
              size="small"
              style={{ 
                borderRadius: '8px',
                fontWeight: '500'
              }}
              aria-label={`${mentor.firstName} ${mentor.lastName} mentor profilini görüntüle`}
            >
              Profili Gör
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderMentorCardSkeleton = () => (
    <Card
      className="app-card"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ 
        padding: '16px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
      }}
    >
      {/* Profil Resmi Skeleton */}
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <Skeleton.Avatar active size={80} shape="circle" style={{ width: '80px', height: '80px' }} />
      </div>
      
      {/* İsim ve Başlık Skeleton */}
      <div style={{ marginBottom: '12px', width: '100%' }}>
        <Skeleton active paragraph={{ rows: 2, width: ['90%', '70%'] }} title={false} style={{ width: '100%' }} />
      </div>
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ width: '100%' }}>
          {/* Fiyat Skeleton */}
          <div style={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Skeleton.Button active size="small" style={{ width: '100px', height: '20px' }} />
          </div>
          
          {/* Deneyim Skeleton */}
          <div style={{ marginBottom: '12px', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Skeleton.Button active size="small" style={{ width: '120px', height: '20px' }} />
          </div>
          
          {/* Rating Skeleton */}
          <div style={{ marginBottom: '12px', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Skeleton.Button active size="small" style={{ width: '150px', height: '20px' }} />
          </div>
          
          {/* Skills Skeleton */}
          <div style={{ marginBottom: '12px', width: '100%', display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
            <Skeleton.Button active size="small" style={{ width: '60px', height: '24px' }} />
            <Skeleton.Button active size="small" style={{ width: '70px', height: '24px' }} />
            <Skeleton.Button active size="small" style={{ width: '65px', height: '24px' }} />
          </div>
          
          {/* Bio Skeleton */}
          <div style={{ marginBottom: '12px', width: '100%' }}>
            <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
          </div>
        </div>
        
        {/* Alt Butonlar Skeleton */}
        <div style={{ marginTop: '16px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Skeleton.Button active size="small" style={{ width: '80px', height: '28px' }} />
          <Skeleton.Button active size="small" style={{ width: '100px', height: '28px' }} />
        </div>
      </div>
    </Card>
  );

  // Aktif filtreleri chip olarak göster
  const activeFilters = useMemo(() => {
    const filters: Array<{ key: string; label: string; onRemove: () => void }> = [];
    if (searchParams.category) {
      filters.push({
        key: 'category',
        label: `Kategori: ${searchParams.category}`,
        onRemove: () => handleFilterChange('category', undefined)
      });
    }
    if (searchParams.skill) {
      filters.push({
        key: 'skill',
        label: `Yetenek: ${searchParams.skill}`,
        onRemove: () => handleFilterChange('skill', undefined)
      });
    }
    if (searchParams.company) {
      filters.push({
        key: 'company',
        label: `Şirket: ${searchParams.company}`,
        onRemove: () => handleFilterChange('company', undefined)
      });
    }
    if (searchParams.minPrice != null || searchParams.maxPrice != null) {
      const minStr = searchParams.minPrice != null ? `₺${searchParams.minPrice}` : '₺0';
      const maxStr = searchParams.maxPrice != null ? `₺${searchParams.maxPrice}` : '∞';
      filters.push({
        key: 'price',
        label: `Fiyat: ${minStr} - ${maxStr}`,
        onRemove: () => {
          setSearchParams(prev => ({ ...prev, minPrice: undefined, maxPrice: undefined, page: 1 }));
          setDrawerFilters(prev => ({ ...prev, minPrice: undefined, maxPrice: undefined }));
        }
      });
    }
    if (searchParams.minExperience != null || searchParams.maxExperience != null) {
      const minStr = searchParams.minExperience != null ? `${searchParams.minExperience}` : '0';
      const maxStr = searchParams.maxExperience != null ? `${searchParams.maxExperience}` : '∞';
      filters.push({
        key: 'experience',
        label: `Deneyim: ${minStr} - ${maxStr} yıl`,
        onRemove: () => {
          setSearchParams(prev => ({ ...prev, minExperience: undefined, maxExperience: undefined, page: 1 }));
          setDrawerFilters(prev => ({ ...prev, minExperience: undefined, maxExperience: undefined }));
        }
      });
    }
    if (searchParams.hasAvailability === true) {
      filters.push({
        key: 'availability',
        label: 'Müsait',
        onRemove: () => handleFilterChange('hasAvailability', undefined)
      });
    }
    return filters;
  }, [searchParams]);

  const hasActiveFilters = activeFilters.length > 0 || searchParams.sortBy != null;

  const renderFilters = () => (
    <div style={{ marginBottom: '24px' }}>
      <div className="app-hero">
        <div className="app-hero-inner">
          <Title level={1} className="app-hero-title">Mentörler</Title>
          {!isCompany && <p className="app-hero-subtitle">Uzman mentorlerle tanışın ve hedeflerinize ulaşın</p>}
        </div>
      </div>

      {/* FilterSortBar */}
      <FilterSortBar
        searchPlaceholder="Mentör ara..."
        searchValue={searchParams.searchTerm || ''}
        onSearchChange={(value) => setSearchParams(prev => ({ ...prev, searchTerm: value, page: 1 }))}
        onSearch={handleSearch}
        categoryOptions={filters?.categories || []}
        categoryValue={searchParams.category}
        onCategoryChange={(value) => handleFilterChange('category', value)}
        sortOptions={SORT_OPTIONS.map(o => ({ value: o.value || '', label: o.label }))}
        sortValue={currentSortValue || undefined}
        onSortChange={handleSortChange}
        showAdvancedFilters={true}
        onAdvancedFiltersOpen={handleDrawerOpen}
        advancedFilterCount={
          (searchParams.minPrice != null || searchParams.maxPrice != null ? 1 : 0) +
          (searchParams.minExperience != null || searchParams.maxExperience != null ? 1 : 0) +
          (searchParams.skill ? 1 : 0) +
          (searchParams.company ? 1 : 0) +
          (searchParams.hasAvailability === true ? 1 : 0)
        }
        onReset={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
        activeFilters={activeFilters}
        mobileBreakpoint="md"
      />

      {/* Sonuç sayısı */}
      {pagination.total > 0 && (
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {pagination.total} mentor bulundu
          </Text>
        </div>
      )}
    </div>
  );

  const renderAdvancedFilters = () => (
    <Drawer
      title="Gelişmiş Filtreler"
      placement="right"
      onClose={() => setFilterDrawerVisible(false)}
      open={filterDrawerVisible}
      width={400}
    >
      {filters && (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Fiyat Aralığı (₺/saat)</Title>
            <Space.Compact style={{ width: '100%', gap: '8px' }}>
              <InputNumber
                placeholder="Min"
                style={{ width: '100%' }}
                min={0}
                max={filters.maxPrice}
                value={drawerFilters.minPrice ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, minPrice: v != null ? Number(v) : undefined }))}
                addonBefore="₺"
              />
              <InputNumber
                placeholder="Max"
                style={{ width: '100%' }}
                min={drawerFilters.minPrice ?? 0}
                max={filters.maxPrice * 2}
                value={drawerFilters.maxPrice ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, maxPrice: v != null ? Number(v) : undefined }))}
                addonBefore="₺"
              />
            </Space.Compact>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Deneyim (Yıl)</Title>
            <Space.Compact style={{ width: '100%', gap: '8px' }}>
              <InputNumber
                placeholder="Min yıl"
                style={{ width: '100%' }}
                min={0}
                max={filters.maxExperience}
                value={drawerFilters.minExperience ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, minExperience: v != null ? Number(v) : undefined }))}
                controls={false}
              />
              <InputNumber
                placeholder="Max yıl"
                style={{ width: '100%' }}
                min={drawerFilters.minExperience ?? 0}
                max={99}
                value={drawerFilters.maxExperience ?? undefined}
                onChange={(v) => setDrawerFilters(prev => ({ ...prev, maxExperience: v != null ? Number(v) : undefined }))}
                controls={false}
              />
            </Space.Compact>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Yetkinlik</Title>
            <Select
              placeholder="Yetkinlik seçin"
              style={{ width: '100%' }}
              value={drawerFilters.skill}
              onChange={(value) => setDrawerFilters(prev => ({ ...prev, skill: value }))}
              allowClear
            >
              {filters.skills.map(skill => (
                <Option key={skill} value={skill}>{skill}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Şirket</Title>
            <Input
              placeholder="Şirket adı girin (örn: Microsoft, Google)"
              style={{ width: '100%' }}
              value={drawerFilters.company || ''}
              onChange={(e) => setDrawerFilters(prev => ({ ...prev, company: e.target.value || undefined }))}
              allowClear
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Space>
              <Switch
                checked={drawerFilters.hasAvailability ?? false}
                onChange={(checked) => setDrawerFilters(prev => ({ ...prev, hasAvailability: checked }))}
              />
              <Text>Sadece müsait mentorler</Text>
            </Space>
          </div>

          <Button type="primary" block onClick={handleDrawerApply}>
            Filtreleri Uygula
          </Button>
        </div>
      )}
    </Drawer>
  );

  const renderBannerAd = (ad: AdvertisementResponse, index: number) => (
    <div
      key={ad.id}
      style={{
        width: '100%',
        marginBottom: '24px',
        cursor: 'pointer',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onClick={() => handleAdClick(ad)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      <img
        src={getAdvertisementImageSrc(ad.imageBase64, ad.imageContentType)}
        alt="Banner Reklam"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block'
        }}
      />
    </div>
  );

  const renderCardAd = (ad: AdvertisementResponse, index: number) => (
    <Card
      key={ad.id}
      hoverable
      style={{
        height: '100%',
        cursor: 'pointer',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
      onClick={() => handleAdClick(ad)}
      cover={
        <img
          alt={ad.title || 'Card Reklam'}
          src={getAdvertisementImageSrc(ad.imageBase64, ad.imageContentType)}
          style={{
            width: '100%',
            height: '200px',
            objectFit: 'cover'
          }}
        />
      }
    >
      {ad.title && (
        <Card.Meta
          title={ad.title}
          description={ad.description}
        />
      )}
    </Card>
  );

  const renderEmptyAdSlot = (type: AdvertisementType, position: string) => {
    if (!isMentor) return null;

    const targetSlot = type === AdvertisementType.Banner ? matchingBannerSlot : matchingCardSlot;
    if (!targetSlot) return null;

    return (
      <div
        style={{
          width: '100%',
          padding: '40px',
          textAlign: 'center',
          border: '2px dashed #d9d9d9',
          borderRadius: '8px',
          marginBottom: '24px',
          background: '#fafafa'
        }}
      >
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Buraya reklam verebilirsiniz
        </Text>
        <br />
        <Button
          type="link"
          onClick={() => {
            storeAdvertisementIntent(targetSlot.id);
            navigate('/profile?tab=advertisements');
          }}
          style={{ marginTop: '8px' }}
        >
          Reklam Yönetimi →
        </Button>
      </div>
    );
  };

  return (
    <div className="app-page">
      <div className="app-container">
        {renderFilters()}

        {/* Banner Reklamlar - Alt Alta */}
        {bannerAds.length > 0 ? (
          <div style={{ marginBottom: '24px' }}>
            {bannerAds.map((ad, index) => (
              <div key={ad.id} style={{ marginBottom: index < bannerAds.length - 1 ? '16px' : '0' }}>
                {renderBannerAd(ad, index)}
              </div>
            ))}
          </div>
        ) : (
          renderEmptyAdSlot(AdvertisementType.Banner, 'top')
        )}

        <Spin spinning={searchLoading}>
          <Row gutter={[16, 16]}>
            {searchLoading && mentors.length === 0 ? (
              // İlk yüklemede skeleton göster
              Array.from({ length: 12 }).map((_, index) => (
                <Col xs={12} sm={12} md={8} lg={6} key={`skeleton-${index}`}>
                  {renderMentorCardSkeleton()}
                </Col>
              ))
            ) : (
              mentors.map((mentor, index) => {
                // Her 6 mentor'dan sonra bir card reklam göster
                const shouldShowAd = cardAds.length > 0 && index > 0 && index % 6 === 0;
                const adIndex = Math.floor(index / 6) - 1;
                const ad = shouldShowAd && adIndex < cardAds.length ? cardAds[adIndex % cardAds.length] : null;

                return (
                  <Fragment key={mentor.id}>
                    {shouldShowAd && ad ? (
                      <Col xs={24} sm={12} md={8} lg={6}>
                        {renderCardAd(ad, adIndex)}
                      </Col>
                    ) : shouldShowAd && !ad && isMentor ? (
                      <Col xs={24} sm={12} md={8} lg={6}>
                        {renderEmptyAdSlot(AdvertisementType.Card, `position-${index}`)}
                      </Col>
                    ) : null}
                    <Col xs={24} sm={12} md={8} lg={6}>
                      {renderMentorCard(mentor)}
                    </Col>
                  </Fragment>
                );
              })
            )}
          </Row>

          {mentors.length === 0 && !searchLoading && (
            <div className="app-empty-card">
              <Empty description="Mentor bulunamadı. Arama kriterlerinizi değiştirerek tekrar deneyin." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </Spin>

        {pagination.total > 0 && (
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} mentor`}
              onShowSizeChange={handlePageChange}
            />
          </div>
        )}

        {renderAdvancedFilters()}
      </div>
    </div>
  );
};

export default MentorSearch;
